import { EMPLOYEE_DESK_EXTERNAL_PORTALS } from '../constants/employeeDeskExternalPortals'
import { employeeDeskPath } from './employeeDeskPath'
import { deskPortalOn } from './employeeDeskPortalPrefs'
import { adminNavTo } from './mailAlertHost'

import type { EmployeeWorkspace } from './employeeWorkspace'

export type DeskNavLang = 'ru' | 'en' | 'id'

const POSITION_TITLE: Record<DeskNavLang, Record<string, string>> = {
  ru: {
    Admin: 'Администратор',
    Manager: 'Менеджер проектов',
    DataAnalyst: 'Аналитик данных',
    ProjectLaunchSpecialist: 'Специалист по запуску проектов',
    Google: 'Google Ads',
    Facebook: 'Meta / Facebook',
    Asistent: 'Ассистент',
    Control: 'Контроль',
    Accounter: 'Бухгалтер',
    Zam: 'Заместитель',
    SEO: 'SEO',
    Designer: 'Дизайнер',
    OperationalDirector: 'Операционный директор',
  },
  en: {
    Admin: 'Administrator',
    Manager: 'Project manager',
    DataAnalyst: 'Data analyst',
    ProjectLaunchSpecialist: 'Project launch specialist',
    Google: 'Google Ads',
    Facebook: 'Meta / Facebook',
    Asistent: 'Assistant',
    Control: 'Control',
    Accounter: 'Accountant',
    Zam: 'Deputy',
    SEO: 'SEO',
    Designer: 'Designer',
    OperationalDirector: 'Operations director',
  },
  id: {
    DataAnalyst: 'Analis data',
    Admin: 'Administrator', Manager: 'Manajer', ProjectLaunchSpecialist: 'Spesialis peluncuran proyek', Google: 'Google Ads', Facebook: 'Meta / Facebook',
    Asistent: 'Asisten', Control: 'Kontrol', Accounter: 'Akuntan', Zam: 'Wakil pimpinan',
    SEO: 'SEO', Designer: 'Desainer', OperationalDirector: 'Direktur operasional',
  },
}

const NAV: Record<DeskNavLang, Record<string, string>> = {
  ru: {
    home: 'Главная',
    clients: 'Клиенты',
    create_client: '\u0421\u043e\u0437\u0434\u0430\u0442\u044c \u043a\u043b\u0438\u0435\u043d\u0442\u0430',
    invoices: 'Инвойсы',
    accounting: 'Бухгалтерия',
    tg: 'TG мониторинг',
    ai: 'ИИ аналитика',
    facebook_metrics: 'Метрики Meta Ads',
    facebook_alerts: 'META ALERT',
    grab: 'Grab POS',
    boards: 'Доска',
    google_ads_audit: 'Аудит Google Ads',
    google_api: 'Google API',
    settings: 'Настройки',
    project_tasks: 'Список задач',
    task_assign: 'Постановка задач',
    tasks: 'Мои задачи',
    creative_calendar: 'Календарь креативов',
    creative_portal: '\u041a\u0440\u0435\u043e\u041f\u043e\u0440\u0442\u0430\u043b',
    cases: '\u041a\u0435\u0439\u0441\u044b',
    assistant: 'Рабочее место ассистента',
    slack: 'Команда / Slack',
  },
  en: {
    home: 'Home',
    clients: 'Clients',
    create_client: 'Create client',
    invoices: 'Invoices',
    accounting: 'Accounting',
    tg: 'TG monitoring',
    ai: 'AI analytics',
    facebook_metrics: 'Meta Ads metrics',
    facebook_alerts: 'META ALERT',
    grab: 'Grab POS',
    boards: 'Board',
    google_ads_audit: 'Google Ads audit',
    google_api: 'Google API',
    settings: 'Settings',
    project_tasks: 'Task list',
    task_assign: 'Task assignment',
    tasks: 'My tasks',
    creative_calendar: 'Creative calendar',
    creative_portal: 'Creative portal',
    cases: 'Cases',
    assistant: 'Assistant workspace',
    slack: 'Team / Slack',
  },
  id: {
    home: 'Beranda', clients: 'Klien', create_client: 'Buat klien', invoices: 'Tagihan', accounting: 'Akuntansi',
    tg: 'Pemantauan TG', ai: 'Analitik AI', facebook_metrics: 'Metrik Meta Ads',
    facebook_alerts: 'PERINGATAN META', grab: 'Grab POS', boards: 'Papan',
    google_ads_audit: 'Audit Google Ads', google_api: 'Google API', settings: 'Pengaturan',
    project_tasks: 'Daftar tugas', task_assign: 'Penugasan', tasks: 'Tugas saya',
    creative_calendar: 'Kalender kreatif', creative_portal: 'Portal Kreatif', cases: 'Studi kasus',
    assistant: 'Ruang kerja asisten',
    slack: 'Tim / Slack',
  },
}

/** Подписи внешних порталов в EN (в константах по умолчанию RU). */
const PORTAL_LABEL_EN: Record<string, string> = {
  portal_grab: 'Grab portal',
  portal_gojek: 'Gojek',
  portal_qr: 'QR dashboard',
}
const PORTAL_LABEL_ID: Record<string, string> = {
  portal_grab: 'Portal Grab',
  portal_gojek: 'Gojek',
  portal_qr: 'Dasbor QR',
}

export type CabinetNavItem = {
  id: string
  label: string
  /** SPA route (react-router) */
  to?: string
  /** Full document navigation (HTML или другой хост) */
  href?: string
  /** Внешний URL (другой origin) — пометка ↗ и без «active» по pathname SPA */
  external?: boolean
  enabled: boolean
}

function deskOn(p: Record<string, boolean> | null | undefined, key: string): boolean {
  if (!p || p[key] === undefined) return true
  return Boolean(p[key])
}

function employeeHtmlHref(path: string, lang: DeskNavLang): string {
  const href = adminNavTo(path)
  const separator = href.includes('?') ? '&' : '?'
  return `${href}${separator}lang=${encodeURIComponent(lang)}`
}

export function employeeCabinetPositionTitle(lang: DeskNavLang, normalized: string | null | undefined): string {
  const k = String(normalized || '').trim()
  if (!k) return lang === 'ru' ? 'Сотрудник' : (lang === 'id' ? 'Karyawan' : 'Employee')
  const map = POSITION_TITLE[lang]
  return map[k] || k
}

/** @deprecated use employeeCabinetPositionTitle('ru', …) */
export function employeeCabinetPositionTitleRu(normalized: string | null | undefined): string {
  return employeeCabinetPositionTitle('ru', normalized)
}

/** Подсветка пунктов меню с полной навигацией (href), синхронно с текущим URL SPA */
export function cabinetNavHrefIsActive(href: string, pathname: string, search: string = ''): boolean {
  const full = `${pathname}${search || ''}`
  const h = String(href || '').trim()
  if (!h) return false
  try {
    if (h.startsWith('http://') || h.startsWith('https://')) {
      const u = new URL(h)
      if (typeof window !== 'undefined' && u.origin !== window.location.origin) {
        return false
      }
      const path = u.pathname
      if (u.search) {
        const want = `${path}${u.search}`
        return full === want || full.startsWith(`${want}/`)
      }
      return pathname === path || pathname.startsWith(`${path}/`)
    }
    const [pathPart, queryPart] = h.split('?')
    const wantPath = pathPart || ''
    if (queryPart) {
      const want = `${wantPath}?${queryPart}`
      return full === want || full.startsWith(`${want}/`)
    }
    return pathname === wantPath || pathname.startsWith(`${wantPath}/`)
  } catch {
    return false
  }
}

/**
 * Пункты бокового меню: для зама — meta.zam_nav_* + desk_show_portal_* (через /api/auth/me → desk_prefs);
 * для остальных ролей — прежняя логика desk_show_* и доступы.
 */
function buildEmployeeCabinetNavBase(input: {
  workspace?: EmployeeWorkspace
  employeeId: string
  normalizedPosition: string | null | undefined
  normalizedPositions?: string[] | null
  deskPrefs: Record<string, boolean> | null | undefined
  canAccessFleetAiAnalytics: boolean
  canAccessTgMonitoring: boolean
  taskBoardV2Assigner: boolean
  /** Серверные права уже объединены по всем должностям сотрудника. */
  permissions?: string[] | null
  /** Язык подписей в боковом меню (синхрон с PortalUiLanguageContext) */
  lang?: DeskNavLang
  /** Админ в кабинете: «Главная» → /home; портфельная ИИ — /admin/system-ai-analytics. */
  adminSpaHome?: boolean
}): CabinetNavItem[] {
  const p = input.deskPrefs
  const eid = String(input.employeeId || '').trim()
  const norm = String(input.normalizedPosition || '').trim() || null
  const isZam = norm === 'Zam'
  const isSeo = norm === 'SEO'
  const lang: DeskNavLang = input.lang === 'id' ? 'id' : (input.lang === 'en' ? 'en' : 'ru')
  if (input.workspace && !input.adminSpaHome) {
    return input.workspace.navigation.map((item) => ({
      id: item.id,
      label: item.label[lang],
      ...(item.spa ? { to: adminNavTo(item.href) } : { href: item.external ? item.href : adminNavTo(item.href) }),
      external: item.external,
      enabled: true,
    }))
  }
  const L = NAV[lang]
  const botItems: CabinetNavItem[] = input.adminSpaHome
    ? [{ id: 'telegram_bot', label: lang === 'ru' ? 'Бот (ТГ)' : 'Bot (TG)', to: adminNavTo('/employee/telegram-bot'), enabled: true }]
    : []
  const slackItem: CabinetNavItem = {
    id: 'slack',
    label: L.slack,
    to: adminNavTo('/employee/slack'),
    enabled: true,
  }
  const permissions = new Set((input.permissions || []).map((value) => String(value || '').trim()))
  const canUseInvoiceActions = permissions.has('invoice.view')
    && permissions.has('invoice.status.edit')
    && permissions.has('invoice.send')
  const storedRoles = input.normalizedPositions ?? (norm ? [norm] : [])
  const canUseAccounting = !input.adminSpaHome && storedRoles.includes('Accounter')
  const accountingItems: CabinetNavItem[] = canUseAccounting
    ? [{ id: 'accounting', label: L.accounting, href: adminNavTo('/employee/accounting'), enabled: true }]
    : []
  if (isSeo) {
    const seoDesk = adminNavTo(employeeDeskPath(eid, 'SEO'))
    return [
      { id: 'home', label: L.home, to: seoDesk, enabled: true },
      slackItem,
      ...botItems,
      ...accountingItems,
      {
        id: 'seo_calendar',
        label: lang === 'ru' ? 'SEO Календарь' : (lang === 'id' ? 'Kalender SEO' : 'SEO Calendar'),
        to: adminNavTo('/seo/react-preview?view=calendar'),
        enabled: true,
      },
      {
        id: 'seo_table',
        label: lang === 'ru' ? 'Таблица' : (lang === 'id' ? 'Artikel' : 'Articles'),
        to: adminNavTo('/seo/table/react-preview?view=articles'),
        enabled: true,
      },
      { id: 'tasks', label: L.tasks, to: adminNavTo('/employee/tasks'), enabled: true },
      { id: 'settings', label: L.settings, href: adminNavTo('/employee/settings'), enabled: true },
    ]
  }
  const isDesigner = norm === 'Designer'
  if (isDesigner) {
    const designerDesk = adminNavTo(employeeDeskPath(eid, 'Designer'))
    return [
      { id: 'home', label: L.home, to: designerDesk, enabled: true },
      slackItem,
      ...botItems,
      ...accountingItems,
      { id: 'tasks', label: L.tasks, to: adminNavTo('/employee/tasks'), enabled: true },
      {
        id: 'creative_calendar',
        label: L.creative_calendar,
        to: adminNavTo('/employee/creative-calendar'),
        enabled: true,
      },
      { id: 'settings', label: L.settings, href: adminNavTo('/employee/settings'), enabled: true },
    ]
  }
  if (!input.adminSpaHome && norm === 'Facebook') {
    return [
      { id: 'home', label: L.home, to: adminNavTo(employeeDeskPath(eid, norm)), enabled: true },
      slackItem,
      ...botItems,
      ...accountingItems,
      { id: 'facebook_metrics', label: L.facebook_metrics, to: adminNavTo('/facebook_api'), enabled: true },
      { id: 'facebook_alerts', label: L.facebook_alerts, to: adminNavTo('/facebook_api/alerts'), enabled: true },
      { id: 'tasks', label: L.tasks, to: adminNavTo('/employee/tasks'), enabled: true },
      { id: 'settings', label: L.settings, href: adminNavTo('/employee/settings'), enabled: true },
    ]
  }
  if (norm === 'Asistent') {
    const assistantItems: CabinetNavItem[] = [
      { id: 'home', label: L.home, href: employeeHtmlHref('/employee/home?legacy=1', lang), enabled: true },
      slackItem,
      ...botItems,
      { id: 'clients', label: L.clients, href: adminNavTo('/employee/clients'), enabled: true },
      ...accountingItems,
      { id: 'invoices', label: lang === 'ru' ? 'Создать инвойс' : (lang === 'id' ? 'Buat invoice' : 'Create invoice'), href: adminNavTo('/employee/invoices#custom_invoice_card'), enabled: true },
      { id: 'tasks', label: L.tasks, to: adminNavTo('/employee/tasks'), enabled: true },
      {
        id: 'aggregators',
        label: lang === 'ru' ? 'Агрегаторы' : (lang === 'id' ? 'Aggregator' : 'Aggregators'),
        href: employeeHtmlHref('/employee/aggregators', lang),
        enabled: true,
      },
      { id: 'settings', label: L.settings, to: adminNavTo('/employee/settings'), enabled: true },
    ]
    return assistantItems
  }
  const deskHome = input.adminSpaHome
    ? adminNavTo('/home')
    : norm === 'SEO'
      ? adminNavTo('/seo_home')
      : adminNavTo(employeeDeskPath(eid, norm))
  const deskHomePathOnly = deskHome.split('?')[0]
  const homeIsSpaDesk = Boolean(input.adminSpaHome) || deskHomePathOnly.includes('/employee/desk/')

  const items: CabinetNavItem[] = []

  if (homeIsSpaDesk) {
    items.push({ id: 'home', label: L.home, to: deskHome, enabled: true })
  } else {
    items.push({ id: 'home', label: L.home, href: deskHome, enabled: true })
  }

  items.push(slackItem)
  items.push(...botItems)

  const grabBase = (import.meta.env.VITE_GRAB_APP_PUBLIC_URL || '').trim().replace(/\/$/, '')

  /** Деск /employee/desk/google|facebook — без вкладки «Клиенты» (инвойсы им не нужны в этом кабинете). */
  const specialistAdsDesk = norm === 'Google' || norm === 'Facebook'
  const showClients = isZam
    ? deskOn(p, 'zam_nav_clients')
    : deskOn(p, 'desk_show_invoices') && !specialistAdsDesk
  if (showClients) {
    items.push({
      id: 'clients',
      label: L.clients,
      href: adminNavTo('/employee/clients'),
      enabled: true,
    })
  }
  if (isZam) {
    items.push({
      id: 'create_client',
      label: L.create_client,
      href: adminNavTo('/employee/add-client'),
      enabled: true,
    })
    items.push({
      id: 'invoices',
      label: L.invoices,
      href: adminNavTo('/admin/invoices?debtors=1'),
      enabled: true,
    })
  }
  if (canUseAccounting && !items.some((item) => item.id === 'accounting')) {
    items.push({
      id: 'accounting',
      label: L.accounting,
      href: adminNavTo('/employee/accounting'),
      enabled: true,
    })
  }
  if (canUseInvoiceActions && !items.some((item) => item.id === 'invoices')) {
    items.push({
      id: 'invoices',
      label: L.invoices,
      href: adminNavTo('/employee/invoices'),
      enabled: true,
    })
  }
  if (isZam && deskOn(p, 'zam_nav_dailies')) {
    items.push({
      id: 'dailies',
      label: lang === 'ru' ? 'Дейлики' : (lang === 'id' ? 'Rutinitas' : 'Dailies'),
      href: adminNavTo('/employee/dailies'),
      enabled: true,
    })
  }


  if (input.canAccessTgMonitoring && (!isZam || deskOn(p, 'zam_nav_tg'))) {
    items.push({
      id: 'tg',
      label: L.tg,
      to: adminNavTo('/employee/monitoring_tg'),
      enabled: true,
    })
  }

  if (input.canAccessFleetAiAnalytics && (!isZam || deskOn(p, 'zam_nav_fleet_ai'))) {
    items.push({
      id: 'ai',
      label: L.ai,
      to: adminNavTo(input.adminSpaHome ? '/admin/system-ai-analytics' : '/employee/system-ai-analytics'),
      enabled: true,
    })
  }

  if (norm === 'Zam' || norm === 'Control') {
    items.push({
      id: 'google_metrics',
      label: L.google_api,
      to: adminNavTo('/google_api'),
      enabled: true,
    })
    if (norm === 'Control') {
      items.push({
        id: 'google_zone',
        label: lang === 'ru' ? '↳ Google зона' : (lang === 'id' ? '↳ Zona Google' : '↳ Google zone'),
        href: adminNavTo('/employee/zones/google'),
        enabled: true,
      })
    }
  }

  if (norm === 'Facebook' || norm === 'Zam' || norm === 'Control') {
    items.push({
      id: 'facebook_metrics',
      label: L.facebook_metrics,
      to: adminNavTo('/facebook_api'),
      enabled: true,
    })
    if (norm === 'Control') {
      items.push({
        id: 'meta_zone',
        label: lang === 'ru' ? '↳ Meta зона' : (lang === 'id' ? '↳ Zona Meta' : '↳ Meta zone'),
        href: adminNavTo('/employee/zones/meta'),
        enabled: true,
      })
    }
    items.push({
      id: 'facebook_alerts',
      label: L.facebook_alerts,
      to: adminNavTo('/facebook_api/alerts'),
      enabled: true,
    })
  }

  if (norm === 'Control') {
    items.push({ id: 'aggregators', label: lang === 'ru' ? 'Агрегаторы' : (lang === 'id' ? 'Aggregator' : 'Aggregators'), href: employeeHtmlHref('/employee/aggregators', lang), enabled: true })
    items.push({ id: 'aggregator_zone', label: lang === 'ru' ? '↳ Зона агрегаторов' : (lang === 'id' ? '↳ Zona aggregator' : '↳ Aggregator zone'), href: adminNavTo('/employee/zones/aggregators'), enabled: true })
  }

  // The deputy needs one management view for delivery clients. Do not expose
  // the separate POS/portal shortcuts because they duplicate the same scope.
  if (isZam) {
    items.push({
      id: 'grab_gojek',
      label: 'Grab / Gojek',
      href: employeeHtmlHref('/employee/aggregators', lang),
      enabled: true,
    })
    items.push({
      id: 'cases',
      label: L.cases,
      to: adminNavTo('/employee/cases'),
      enabled: true,
    })
  } else if (grabBase) {
    items.push({
      id: 'grab',
      label: L.grab,
      href: grabBase,
      external: true,
      enabled: true,
    })
  }

  for (const portal of EMPLOYEE_DESK_EXTERNAL_PORTALS) {
    const hiddenForZam = portal.id === 'portal_grab' || portal.id === 'portal_gojek'
    if ((!isZam || !hiddenForZam) && deskPortalOn(p, portal.prefKey)) {
      items.push({
        id: portal.id,
        label: lang === 'en' ? PORTAL_LABEL_EN[portal.id] || portal.label : (lang === 'id' ? PORTAL_LABEL_ID[portal.id] || portal.label : portal.label),
        href: portal.href,
        external: true,
        enabled: true,
      })
    }
  }

  const googleDeskOnly = norm === 'Google'
  const controlDesk = norm === 'Control'

  if (!isZam && !googleDeskOnly && !controlDesk) {
    items.push({
      id: 'project_tasks',
      label: L.project_tasks,
      href: adminNavTo('/employee/tasks'),
      enabled: true,
    })
  }

  if (!isZam || deskOn(p, 'zam_nav_my_tasks_v2')) {
    if (norm === 'Google') {
      items.push({
        id: 'google_api',
        label: L.google_api,
        to: adminNavTo('/google_api'),
        enabled: true,
      })
      items.push({
        id: 'clients',
        label: L.clients,
        href: adminNavTo('/employee/google'),
        enabled: true,
      })
    }
    items.push({
      id: 'tasks',
      label: L.tasks,
      to: adminNavTo('/employee/tasks'),
      enabled: true,
    })
    if (norm === 'Google') {
      items.push({
        id: 'settings',
        label: L.settings,
        href: adminNavTo('/employee/settings'),
        enabled: true,
      })
    }
  }

  if (norm === 'Control') {
    items.push({
      id: 'creative_calendar',
      label: L.creative_calendar,
      to: adminNavTo('/employee/creative-calendar'),
      enabled: true,
    })
    items.push({
      id: 'creative_zone',
      label: lang === 'ru' ? '↳ Крео зона' : (lang === 'id' ? '↳ Zona kreatif' : '↳ Creative zone'),
      href: adminNavTo('/employee/zones/creative'),
      enabled: true,
    })
  } else if (isZam && deskOn(p, 'zam_nav_creative_portal')) {
    items.push({
      id: 'creative_calendar',
      label: L.creative_portal,
      to: adminNavTo('/employee/creative-calendar'),
      enabled: true,
    })
  }
  if (!items.some((item) => item.id === 'settings')) {
    items.push({ id: 'settings', label: L.settings, href: adminNavTo('/employee/settings'), enabled: true })
  }

  return items.filter((x) => x.enabled)
}

export function buildEmployeeCabinetNav(input: Parameters<typeof buildEmployeeCabinetNavBase>[0]): CabinetNavItem[] {
  // Filter the server workspace too: a cached response may still contain the old staff link.
  // Navigation visibility is separate from inbox API access and task delivery.
  const items = buildEmployeeCabinetNavBase(input).filter(item => input.adminSpaHome || item.id !== 'telegram_bot')
  if ((input.adminSpaHome || input.permissions?.includes('analytics.fix_zone')) && !items.some(item => item.id === 'fix_zone')) {
    items.splice(1, 0, { id: 'fix_zone', label: input.lang === 'en' ? 'Fix Zone' : input.lang === 'id' ? 'Zona Fix' : 'Зона Фикс',
      to: adminNavTo(input.adminSpaHome ? '/admin/fix-zone' : '/employee/fix-zone'), enabled: true })
  }
  return items
}
